import axios from "axios";
import Modal from "component/Common/LoaderModal";
import { history } from "helpers";
import { getbaseUrl } from "./baserul";
import endpoints from "./endpoints";
import { resetEncryptionKey } from "./fieldEncryption";
import { Toast } from "./toast";

let refreshPromise = null;

const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshUrl = `${getbaseUrl()}/auth/refresh`;
    const callRefresh = async (retryCount = 0) => {
      try {
        const res = await axios.post(
          refreshUrl,
          {},
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        );
        const newToken = res?.data?.data?.token;
        if (newToken) {
          localStorage.setItem("token", newToken);
          return newToken;
        }
        throw new Error("Invalid refresh response");
      } catch (err) {
        const status = err?.response?.status;
        const errData = err?.response?.data;
        const reason = errData?.data?.reason || errData?.reason;

        // 409 REFRESH_RACE: another tab refreshed simultaneously -> retry once
        if (status === 409 && retryCount < 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          return callRefresh(retryCount + 1);
        }

        if (status === 401) {
          if (reason === "LOGGED_IN_OTHER_DEVICE") {
            Toast({
              type: "error",
              message: "You have been logged in from another device.",
            });
          } else {
            Toast({
              type: "error",
              message: "Session expired. Please log in again.",
            });
          }
          localStorage.clear();
          history.replace("/login");
        }
        throw err;
      }
    };

    return await callRefresh();
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

export const request = ({
  url = "",
  method = endpoints.APIMethods.GET,
  data,
  isLoader = true,
  baseUrl = "user",
  responseType,
  token = localStorage.getItem("token"),
  params,
  contentType,
  errorToast = true,
  successToast = true,
  statusCode = true,
  successMsg,
}) =>
  new Promise((resolve, reject) => {
    let config = {
      url: getbaseUrl(baseUrl) + url,
      method: method,
      params: params ? params : null,
      data: data ? data : null,
      responseType: responseType ? responseType : null,
      withCredentials: true,
      headers: {
        Authorization: token ? "Bearer " + token : "",
        "Content-Type": contentType || "application/json",
      },
    };
    config.params == null && delete config.params;
    config.data == null && delete config.data;
    config.responseType == null && delete config.responseType;

    showLoader(isLoader);

    axios(config)
      .then((response) => {
        let {
          data: { message },
        } = response;
        showLoader(false);
        method !== "GET" &&
          successToast &&
          Toast({ type: "success", message: successMsg || message });
        return resolve(response);
      })
      .catch(async (error) => {
        showLoader(false);
        const response = error?.response;
        if (response) {
          let {
            status,
            data: responseData,
          } = response;
          const message = responseData?.message || "";
          const reason = responseData?.data?.reason || responseData?.reason;

          // Handle key rotation / decryption error
          if (reason === "ENCRYPTION_INVALID") {
            resetEncryptionKey();
          }

          // Handle access token expiration with automatic refresh
          if (status === 401 && reason === "ACCESS_TOKEN_EXPIRED") {
            try {
              const newToken = await refreshAccessToken();
              config.headers.Authorization = "Bearer " + newToken;
              showLoader(isLoader);
              const retryResponse = await axios(config);
              showLoader(false);
              const retryMsg = retryResponse?.data?.message;
              method !== "GET" &&
                successToast &&
                Toast({ type: "success", message: successMsg || retryMsg });
              return resolve(retryResponse);
            } catch (refreshError) {
              showLoader(false);
              return reject(refreshError?.response || refreshError);
            }
          }

          if (status === 401) {
            if (reason === "LOGGED_IN_OTHER_DEVICE") {
              Toast({
                type: "error",
                message: "You have been logged in from another device.",
              });
            } else if (reason === "SESSION_EXPIRED") {
              Toast({
                type: "error",
                message: "Session expired. Please log in again.",
              });
            }
            if (window.location.pathname !== "/login") {
              localStorage.clear();
              return history.replace("/login");
            }
          }

          errorToast &&
            Toast({
              type: "error",
              message: message
                ? message
                : "Not able to connect the server. Please try again later",
            });
        } else {
          Toast({
            type: "error",
            message: "Not able to connect the server. Please try again later",
          });
        }

        return reject(response || error);
      });
  });

const showLoader = (status) => {
  if (Modal && Modal.render && Modal.render.defaultProps) {
    Modal.render.defaultProps.setLoaderStatus(status);
  }
};

